import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface ApiError {
  code: number;
  message: string;
}

export interface UserFriendlyError {
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info';
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {

  private errorMapping: { [key: string]: UserFriendlyError } = {
    // OTP related errors
    'No se pudo enviar el código OTP': {
      title: 'Error al enviar código',
      message: 'No pudimos enviar el código de verificación. Esto puede deberse a que:\n\n• El servicio no está disponible temporalmente\n• Su email no está registrado en el sistema\n\nPor favor, verifique su email e intente nuevamente.',
      type: 'error'
    },
    'Código OTP inválido': {
      title: 'Código incorrecto',
      message: 'El código ingresado no es válido. Por favor verifique:\n• Que haya ingresado los 6 dígitos correctamente\n• Que el código no haya expirado\n• Que esté usando el código más reciente enviado',
      type: 'error'
    },
    'OTP expirado': {
      title: 'Código expirado',
      message: 'El código de verificación ha expirado. Por favor solicite un nuevo código.',
      type: 'warning'
    },
    
    // Authentication errors
    'Usuario no encontrado': {
      title: 'Usuario no registrado',
      message: 'No encontramos una cuenta asociada a este email. Si es un proveedor de Arcelor Mittal, puede solicitar acceso usando el botón "Solicitar acceso".',
      type: 'info'
    },
    'Credenciales inválidas': {
      title: 'Error de autenticación',
      message: 'Las credenciales proporcionadas no son válidas. Por favor verifique su información e intente nuevamente.',
      type: 'error'
    },
    
    // Network and server errors
    'Error de conexión': {
      title: 'Error de conexión',
      message: 'No pudimos conectar con el servidor. Por favor verifique su conexión a internet e intente nuevamente.',
      type: 'error'
    },
    'Servicio no disponible': {
      title: 'Servicio temporalmente no disponible',
      message: 'El servicio se encuentra temporalmente no disponible. Por favor intente nuevamente en unos minutos.',
      type: 'warning'
    }
  };

  /**
   * Transforms a server error into a user-friendly error message
   */
  handleError(error: any): UserFriendlyError {
    console.error('Original error:', error);

    // Handle HttpErrorResponse
    if (error instanceof HttpErrorResponse) {
      return this.handleHttpError(error);
    }

    // Handle API error format
    if (error && typeof error === 'object' && 'message' in error) {
      return this.handleApiError(error);
    }

    // Handle string errors
    if (typeof error === 'string') {
      return this.handleStringError(error);
    }

    // Default fallback error
    return {
      title: 'Error inesperado',
      message: 'Ha ocurrido un error inesperado. Por favor intente nuevamente o contacte al soporte técnico si el problema persiste.',
      type: 'error'
    };
  }

  private handleHttpError(error: HttpErrorResponse): UserFriendlyError {
    // Check if the error response contains our API error format
    if (error.error && error.error.message) {
      return this.handleApiError(error.error);
    }

    // Handle common HTTP status codes
    switch (error.status) {
      case 0:
        return this.errorMapping['Error de conexión'];
      case 400:
        return {
          title: 'Solicitud inválida',
          message: 'Los datos enviados no son válidos. Por favor verifique la información e intente nuevamente.',
          type: 'error'
        };
      case 401:
        return this.errorMapping['Credenciales inválidas'];
      case 404:
        return this.errorMapping['Usuario no encontrado'];
      case 500:
        return this.errorMapping['Servicio no disponible'];
      case 503:
        return this.errorMapping['Servicio no disponible'];
      default:
        return {
          title: `Error del servidor (${error.status})`,
          message: 'Ha ocurrido un error en el servidor. Por favor intente nuevamente más tarde.',
          type: 'error'
        };
    }
  }

  private handleApiError(error: ApiError): UserFriendlyError {
    const message = error.message || '';
    
    // Try to find a specific mapping based on message content
    for (const [key, userError] of Object.entries(this.errorMapping)) {
      if (message.includes(key) || message.toLowerCase().includes(key.toLowerCase())) {
        return userError;
      }
    }

    // Handle specific error codes
    switch (error.code) {
      case 400:
        return {
          title: 'Datos incorrectos',
          message: message || 'Los datos proporcionados no son válidos.',
          type: 'error'
        };
      case 401:
        return this.errorMapping['Credenciales inválidas'];
      case 404:
        return this.errorMapping['Usuario no encontrado'];
      case 500:
        return {
          title: 'Error del servidor',
          message: message || 'Ha ocurrido un error interno del servidor.',
          type: 'error'
        };
      default:
        return {
          title: 'Error',
          message: message || 'Ha ocurrido un error inesperado.',
          type: 'error'
        };
    }
  }

  private handleStringError(error: string): UserFriendlyError {
    // Try to find a specific mapping
    for (const [key, userError] of Object.entries(this.errorMapping)) {
      if (error.includes(key) || error.toLowerCase().includes(key.toLowerCase())) {
        return userError;
      }
    }

    return {
      title: 'Error',
      message: error,
      type: 'error'
    };
  }

  /**
   * Checks if an error is related to connectivity issues
   */
  isNetworkError(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 0 || error.status === 503;
    }
    return false;
  }

  /**
   * Suggests retry for certain types of errors
   */
  shouldRetry(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      // Retry for network errors and 5xx server errors
      return error.status === 0 || (error.status >= 500 && error.status <= 599);
    }
    return false;
  }
}